/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { makeObservable, observable, computed } from "mobx"

import { BaseModel } from "./base.model"

import type { AxiosError } from "axios"
import type { LanguageISO, SearchRequestCreationData } from "@providers"
import type { DocumentSerializerOutput } from "@serializers/documents"

export class FaqModel extends BaseModel {
  // @ts-ignore
  constructor(params) {
    super(params)
    makeObservable(this)
  }

  @observable state = "idle" as State
  @observable data = [] as DocumentSerializerOutput[]
  @observable dataWithSearchHighlighting = [] as DocumentSerializerOutput[]
  @observable translations = [] as Translation[]
  @observable search_query_saving_is: boolean = true

  @computed get isUnavailableSearchQuerySaving() {
    return this.search_query_saving_is === false
  }

  @computed get searchResultCount() {
    const re = new RegExp("</mark>", "gi")
    return this.dataWithSearchHighlighting.reduce((acc, item) => {
      const titleMatches = item.title.match(re)
      const descriptionMatches = item.description.match(re)
      if (titleMatches) {
        acc = acc + titleMatches.length
      }
      if (descriptionMatches) {
        acc = acc + descriptionMatches.length
      }
      return acc
    }, 0)
  }

  @computed get tableOfContents() {
    return this.data.reduce<FaqMenu[]>(
      (acc, item) =>
        // @ts-ignore
        acc.concat({
          id: `faq-${item.id}`,
          title: item.title,
          slug: item.slug,
        }),
      [],
    )
  }

  hydrate = (data: unknown) => {
    // @ts-ignore
    this.applyData({ data: data?.data })
    // @ts-ignore
    this.applyData({ translations: data?.translations })
    this.applyData({
      // @ts-ignore
      search_query_saving_is: data?.search_query_saving_is,
    })
    this.applyData({
      // @ts-ignore
      dataWithSearchHighlighting: data?.data,
    })
  }

  serialize = () => {
    return {
      data: this.data,
      dataWithSearchHighlighting: this.data,
      translations: this.translations,
      search_query_saving_is: this.search_query_saving_is,
    }
  }

  readData = async (lang?: LanguageISO) => {
    try {
      const data = await this.Providers.GlobalProvider.getFaq(
        lang || this.Models.GlobalModel.lang,
      )
      this.applyData({ data, dataWithSearchHighlighting: data })
    } catch {}
  }

  readSettings = async (lang?: LanguageISO) => {
    try {
      const {
        translations,
        search_query_saving_is,
      } = await this.Providers.GlobalProvider.getFaqPage(
        lang || this.Models.GlobalModel.lang,
      )
      this.applyData({
        translations,
        search_query_saving_is,
      })
    } catch {}
  }

  init = async (lang?: LanguageISO) => {
    await this.readSettings(lang)
    await this.readData(lang)
  }

  // TODO: нужно пересмотреть способ хранения данных с подстветкой, но обязательно нужно сохранять initialData
  search = async (query: string) => {
    if (query.length) {
      // @ts-ignore
      this.dataWithSearchHighlighting.clear()
      const escaped_query = escapeRegExp(query)
      const re = new RegExp("(?!>)(" + escaped_query + "(?![^<>]*>))", "gi")
      this.data.forEach((item) => {
        const result = {
          ...item,
          title: item.title.replaceAll(re, highlighter),
          description: item.description.replaceAll(re, highlighter),
        }
        this.dataWithSearchHighlighting.push(result)
      })
      return
    }
    // @ts-ignore
    this.dataWithSearchHighlighting.clear()
    this.data.forEach((item) => this.dataWithSearchHighlighting.push(item))
  }

  getTextByKey = (key: string) => {
    return (
      this.translations.find((translation) => translation.key === key)?.value ??
      ""
    )
  }

  saveSearchQuery = async (query: string) => {
    if (this.isUnavailableSearchQuerySaving || query.length < 1) return

    this.state = "pending"
    const data = {
      query,
      type: "faq",
      locale: this.Models.GlobalModel.lang,
    } as SearchRequestCreationData

    try {
      await this.Providers.SearchProvider.create(data)
      this.state = "succeed"
    } catch (err) {
      if ((err as AxiosError).response) {
        const error = err as AxiosError
        console.error(error.response) // eslint-disable-line no-console
      }
      this.state = "failed"
    }
  }
}

function highlighter(str: string) {
  return "<mark>" + str + "</mark>"
}

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

export interface FaqMenu {
  id: string
  title: string
  slug?: string
}

export interface Translation {
  id: number
  key: string
  value: string
}

type State = "idle" | "failed" | "succeed" | "pending"
